const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api'

function getToken() {
  return localStorage.getItem('token')
}

function authHeaders() {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function request(method, path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body != null ? JSON.stringify(body) : undefined,
  })
  if (res.status === 401) {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    window.location.href = '/login'
    throw new Error('Sessão expirada')
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Erro desconhecido' }))
    throw new Error(err.detail || 'Erro na requisição')
  }
  if (res.status === 204) return null
  return res.json()
}

function currentSafraId() {
  const v = localStorage.getItem('currentSafraId')
  return v ? Number(v) : null
}

// Monta querystring com fazenda_id e (opcionalmente) safra_id do contexto atual.
// safra_id é lido do localStorage; endpoints que não usam safra simplesmente o ignoram.
function fq(path, fazendaId, includeSafra = true) {
  const params = []
  if (fazendaId != null) params.push(`fazenda_id=${fazendaId}`)
  const sid = includeSafra ? currentSafraId() : null
  if (sid != null) params.push(`safra_id=${sid}`)
  return params.length ? `${path}?${params.join('&')}` : path
}

export const api = {
  // Auth
  login: (username, password) =>
    request('POST', '/auth/login', { username, password }),

  // Fazendas (admin only)
  getFazendas: () => request('GET', '/fazendas/'),
  createFazenda: (data) => request('POST', '/fazendas/', data),
  updateFazenda: (id, data) => request('PUT', `/fazendas/${id}`, data),
  deleteFazenda: (id) => request('DELETE', `/fazendas/${id}`),

  // Safras (escopadas por fazenda; não anexar safra_id na própria listagem de safras)
  getSafras: (fazendaId) => request('GET', fq('/safras/', fazendaId, false)),
  createSafra: (data, fazendaId) => request('POST', fq('/safras/', fazendaId, false), data),
  updateSafra: (id, data, fazendaId) => request('PUT', fq(`/safras/${id}`, fazendaId, false), data),
  deleteSafra: (id, fazendaId) => request('DELETE', fq(`/safras/${id}`, fazendaId, false)),

  // Areas (fazenda_id obrigatório)
  getAreas: (fazendaId, includeArchived = false, uso = null) =>
    request('GET', fq('/areas/', fazendaId)
      + (includeArchived ? '&include_archived=true' : '')
      + (uso ? `&uso=${encodeURIComponent(uso)}` : '')),
  createArea: (data, fazendaId) => request('POST', fq('/areas/', fazendaId), data),
  updateArea: (id, data, fazendaId) => request('PUT', fq(`/areas/${id}`, fazendaId), data),
  archiveArea: (id, fazendaId) => request('POST', fq(`/areas/${id}/archive`, fazendaId)),
  restoreArea: (id, fazendaId) => request('POST', fq(`/areas/${id}/restore`, fazendaId)),
  deleteArea: (id, fazendaId) => request('DELETE', fq(`/areas/${id}`, fazendaId)),
  reorderAreas: (items, fazendaId) => request('PUT', fq('/areas/reorder', fazendaId), items),

  // Products
  // products: { product_name, dose, unit }[]
  setProducts: (areaId, activityKey, products, fazendaId) =>
    request('PUT', fq(`/areas/${areaId}/products`, fazendaId), { activity_key: activityKey, products }),

  // Aplica recomendação (lista de produtos com dose) a múltiplas áreas de uma vez
  bulkSetProducts: (activityKey, products, areaIds, fazendaId) =>
    request('POST', fq('/areas/bulk-products', fazendaId), { activity_key: activityKey, products, area_ids: areaIds }),

  // Activity records
  recordActivity: (areaId, activityKey, done, usedProducts, notes, performedAt, fazendaId) =>
    request('POST', fq(`/areas/${areaId}/activities`, fazendaId), {
      activity_key: activityKey,
      done,
      used_products: usedProducts,
      notes,
      performed_at: performedAt,
    }),

  // Planned dates (admin only)
  setPlannedDate: (areaId, activityKey, plannedDate, fazendaId) =>
    request('PUT', fq(`/areas/${areaId}/planned-date`, fazendaId), { activity_key: activityKey, planned_date: plannedDate }),

  // Activity configs (globais, sem fazenda_id)
  getActivities: () => request('GET', '/activities/'),
  createActivity: (data) => request('POST', '/activities/', data),
  updateActivity: (id, data) => request('PUT', `/activities/${id}`, data),
  reorderActivities: (items) => request('PUT', '/activities/reorder', items),
  deleteActivity: (id) => request('DELETE', `/activities/${id}`),

  // Calendar rules (admin only)
  getCalendarRules: () => request('GET', '/calendar-rules/'),
  createCalendarRule: (data) => request('POST', '/calendar-rules/', data),
  updateCalendarRule: (id, data) => request('PUT', `/calendar-rules/${id}`, data),
  deleteCalendarRule: (id) => request('DELETE', `/calendar-rules/${id}`),

  // Users
  getUsers: (fazendaId) => request('GET', fq('/users/', fazendaId)),
  createUser: (data) => request('POST', '/users/', data),
  updateUser: (id, data) => request('PUT', `/users/${id}`, data),
  deleteUser: (id) => request('DELETE', `/users/${id}`),

  // Machines (mecanização)
  getMachines: (fazendaId) => request('GET', fq('/machines/', fazendaId)),
  createMachine: (data, fazendaId) => request('POST', fq('/machines/', fazendaId), data),
  updateMachine: (id, data, fazendaId) => request('PUT', fq(`/machines/${id}`, fazendaId), data),
  deleteMachine: (id, fazendaId) => request('DELETE', fq(`/machines/${id}`, fazendaId)),

  getMachineUsage: (params, fazendaId) => {
    const all = { ...params, ...(fazendaId != null ? { fazenda_id: fazendaId } : {}) }
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(all).filter(([, v]) => v != null && v !== ''))
    ).toString()
    return request('GET', `/machines/usage${qs ? `?${qs}` : ''}`)
  },
  createMachineUsage: (data, fazendaId) => request('POST', fq('/machines/usage', fazendaId), data),
  updateMachineUsage: (id, data, fazendaId) => request('PUT', fq(`/machines/usage/${id}`, fazendaId), data),
  deleteMachineUsage: (id, fazendaId) => request('DELETE', fq(`/machines/usage/${id}`, fazendaId)),

  // Insumos (escopados por safra)
  getInsumos: (params, fazendaId) => {
    const sid = currentSafraId()
    const all = {
      ...params,
      ...(fazendaId != null ? { fazenda_id: fazendaId } : {}),
      ...(sid != null ? { safra_id: sid } : {}),
    }
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(all).filter(([, v]) => v != null && v !== ''))
    ).toString()
    return request('GET', `/insumos/${qs ? `?${qs}` : ''}`)
  },
  createInsumo: (data, fazendaId) => request('POST', fq('/insumos/', fazendaId), data),
  updateInsumo: (id, data, fazendaId) => request('PUT', fq(`/insumos/${id}`, fazendaId), data),
  deleteInsumo: (id, fazendaId) => request('DELETE', fq(`/insumos/${id}`, fazendaId)),

  getMachineMaintenance: (params, fazendaId) => {
    const all = { ...params, ...(fazendaId != null ? { fazenda_id: fazendaId } : {}) }
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(all).filter(([, v]) => v != null && v !== ''))
    ).toString()
    return request('GET', `/machines/maintenance${qs ? `?${qs}` : ''}`)
  },
  createMaintenance: (data, fazendaId) => request('POST', fq('/machines/maintenance', fazendaId), data),
  updateMaintenance: (id, data, fazendaId) => request('PUT', fq(`/machines/maintenance/${id}`, fazendaId), data),
  deleteMaintenance: (id, fazendaId) => request('DELETE', fq(`/machines/maintenance/${id}`, fazendaId)),
}
